import { useEffect, useState } from "react";

import { Button } from "@shared/components/ui/Button";
import { ImageUploader } from "@shared/components/ui/ImageUploader";
import { useProfile } from "@shared/hooks/useProfile";
import { MediaFile } from "@shared/types/media.types";
import { User } from "@shared/types/user";
import theme from "@shared/utils/themeColors";
import Toast3 from "../../../components/Toast3";
import { PATHS } from "../../../config/paths";
import { useAuthDetails } from "../hooks/useAuthDetails";
import { UpdateProfileDto } from "../types/settings.types";
import PasswordForm from "./PasswordForm";

interface ProfileFormProps {
  user: User;
}

export default function ProfileForm({
  user: { profile, email },
}: ProfileFormProps) {
  const [formData, setFormData] = useState<UpdateProfileDto>({
    profile: {
      ...profile,
      summary: profile?.summary || "",
      location: profile?.location || "",
      title: profile?.title || "",
      name: profile?.name || "",
      aboutMe: profile?.aboutMe || "",
    },
  });

  useEffect(() => {
    setFormData({
      profile: {
        ...profile,
        summary: profile?.summary || "",
        location: profile?.location || "",
        title: profile?.title || "",
        name: profile?.name || "",
        aboutMe: profile?.aboutMe || "",
      },
    });
  }, [profile]);

  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const { authDetails, isLoading: isLoadingAuth } = useAuthDetails();
  const { uploadImage, isUploading, updateProfile, isUpdating } = useProfile();

  // Handle logo removal
  const handleLogoRemoved = () => {
    setFormData((prev) => ({
      ...prev,
      profile: { ...prev.profile, thumbnailUrl: null, profileImage: null },
    }));
  };

  const handleImageSelected = async (file: MediaFile) => {
    setFormData((prev) => ({
      ...prev,
      profile: { ...prev.profile, thumbnailUrl: file.preview },
    }));
    uploadImage(file.file);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      profile: {
        ...prev.profile,
        [name]: value,
      },
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfile(formData);
  };

  const triggerSubmit = () => {
    updateProfile(formData);
  };

  return (
    <div className="grow">
      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center mb-5">
          <h2 className={`text-2xl ${theme.text.primary} font-bold`}>
            My Account
          </h2>
          <Button
            as="navlink"
            to={PATHS.COMMUNITY.MY_PROFILE}
            variant="tertiary"
            size="sm"
          >
            View Profile
          </Button>
        </div>
        {/* Image */}
        <section>
          <ImageUploader
            className="w-32 h-32"
            imageUrl={formData.profile.thumbnailUrl}
            onImageSelected={handleImageSelected}
            onImageRemoved={() => handleLogoRemoved()}
            disabled={isUpdating}
            isUploading={isUploading}
          />
        </section>

        <form onSubmit={handleSubmit}>
          <section>
            <h2
              className={`text-xl leading-snug ${theme.text.primary} font-bold mb-1`}
            >
              Profile
            </h2>
            <div className="text-sm">
              You can update your profile information here.
            </div>

            <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
              {/* Name */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="name"
                >
                  Name <span className="text-rose-500">*</span>
                </label>
                <input
                  id="name"
                  name="name"
                  className="form-input w-full"
                  type="text"
                  value={formData.profile.name || ""}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Email */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="email"
                >
                  Email
                </label>
                <input
                  id="email"
                  className="form-input w-full dark:disabled:placeholder:text-gray-600 disabled:border-gray-200 dark:disabled:border-gray-700 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600 disabled:cursor-not-allowed shadow-none"
                  type="text"
                  placeholder="Email"
                  value={email}
                  disabled
                />
              </div>
            </div>
            <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
              {/* Your role */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="role"
                >
                  Your role
                </label>
                <input
                  id="role"
                  name="role"
                  className="form-input w-full"
                  type="text"
                  value={formData.profile.role || ""}
                  onChange={handleChange}
                />
              </div>

              {/* Title */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="title"
                >
                  Title
                </label>
                <input
                  id="title"
                  name="title"
                  className="form-input w-full"
                  type="text"
                  value={formData.profile.title || ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
              {/* Location */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="location"
                >
                  Location
                </label>
                <input
                  id="location"
                  name="location"
                  className="form-input w-full"
                  type="text"
                  value={formData.profile.location || ""}
                  onChange={handleChange}
                />
              </div>

              {/* Birthday */}
              <div className="sm:w-1/2">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="birthday"
                >
                  Birthday
                </label>
                <input
                  id="birthday"
                  name="birthday"
                  className="form-input w-full"
                  type="date"
                  value={formData.profile.birthday}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="sm:flex sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-5">
              {/* About Me */}
              <div className="w-full">
                <label
                  className="block text-sm font-medium mb-1"
                  htmlFor="aboutMe"
                >
                  About Me
                </label>
                <textarea
                  id="aboutMe"
                  name="aboutMe"
                  className="form-textarea w-full"
                  rows={4}
                  value={formData.profile.aboutMe}
                  onChange={handleChange}
                />
              </div>
            </div>
          </section>
        </form>

        {/* Password */}
        <section>
          <h2
            className={`text-xl leading-snug ${theme.text.primary} font-bold mb-1`}
          >
            Password
          </h2>
          <div className="text-sm">
            {authDetails?.provider === "google" ? (
              <Toast3
                open={true}
                setOpen={() => {}}
                className={undefined}
                type={undefined}
                closable={false}
              >
                You signed up with Google. You can't set up a password.
              </Toast3>
            ) : (
              <span>You can change your password here.</span>
            )}
          </div>
          {authDetails?.provider !== "google" ? (
            <div className="mt-5">
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setPasswordModalOpen(true);
                }}
                variant="tertiary"
                size="sm"
              >
                {authDetails?.hasPassword ? "Change Password" : "Set Password"}
              </Button>

              <PasswordForm
                modalOpen={passwordModalOpen}
                setModalOpen={setPasswordModalOpen}
                isGoogleUser={false}
                hasPassword={authDetails?.hasPassword}
              />
            </div>
          ) : null}
        </section>
      </div>
      {/* Panel footer */}
      <footer>
        <div
          className={`flex flex-col px-6 py-5 border-t ${theme.border.primary}`}
        >
          <div className="flex self-end">
            <Button type="submit" disabled={isUpdating} onClick={triggerSubmit}>
              {isUpdating ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
